import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/header/Header.tsx';
import Select from '../components/select/Select.tsx';
import { cities, ROUTE_NAME_OPTIONS, timeZone } from '../utils/constants.ts';
import useTextareaAutoHeight from '../utils/useTextareaAutoHeight.ts';
import './user-info-form.scss';

//Token ssh
// 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

interface UserInfo {
  name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  description: string;
  timeSlot: string;
}

interface UserInfoFormProps {
  userInfo: UserInfo;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  children?: ReactNode;
  scheduleRef?: React.RefObject<HTMLDivElement>;
  invalidFields?: string[];
  paymentSection?: ReactNode; // Sección de medio de pago desde ShoppingCart
}

const UserInfoForm = ({
  userInfo,
  handleInputChange,
  children,
  scheduleRef,
  invalidFields,
  paymentSection,
}: UserInfoFormProps) => {
  const { textareaRef } = useTextareaAutoHeight(userInfo.description);
  const location = useLocation();

  const isUserInfoScreen =
    location.pathname === `/${ROUTE_NAME_OPTIONS.USER_INFO}`;

  const handleTimeSlot = (value: string | undefined) => {
    if (value) {
      handleInputChange({
        target: {
          name: 'timeSlot',
          value: value,
        },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    handleInputChange({
      target: {
        name: 'city',
        value: value,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <div>
      {location.pathname !== '/shopping-cart' && <Header />}

      <div
        className={`user-data-form${
          isUserInfoScreen ? ' user-data-form--profile' : ''
        }`}
      >
        {isUserInfoScreen && (
          <h2 className="product-title user-form-title">
            Actualizá tu información personal
          </h2>
        )}

        <section className="flex-column">
          <h3 className="product-title">Información de Contacto</h3>
          <div className="section-card">
            <div className="flex-align-center" data-field="name">
              <label className="label">Nombre:</label>
              <input
                required
                type="text"
                name="name"
                placeholder="nombre completo..."
                className={`input${invalidFields?.includes('name') ? ' input-error' : ''}`}
                value={userInfo.name}
                onChange={handleInputChange}
              />
            </div>
            <div className="flex-align-center" data-field="phone">
              <label className="label">Teléfono:</label>
              <input
                required
                type="tel"
                name="phone"
                placeholder="teléfono..."
                className={`input${invalidFields?.includes('phone') ? ' input-error' : ''}`}
                value={userInfo.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="flex-align-center">
              <label className="label">Email:</label>
              <input
                required
                type="email"
                name="email"
                placeholder="email..."
                className="input input-readonly"
                value={userInfo.email}
                disabled
                onChange={handleInputChange}
              />
            </div>
          </div>
        </section>

        <section className="flex-column">
          <h3 className="product-title">Información de Envío</h3>
          <div className="section-card">
            <div className="flex-align-center" data-field="address">
              <label className="label">Dirección:</label>
              <input
                required
                type="text"
                name="address"
                placeholder="dirección..."
                className={`input${invalidFields?.includes('address') ? ' input-error' : ''}`}
                value={userInfo.address}
                onChange={handleInputChange}
              />
            </div>
            <div className="flex-align-center" data-field="city">
              <label className="label">Ciudad:</label>
              <Select
                className="timezone"
                required
                placeholder="Selecciona tu ciudad"
                options={cities}
                value={cities.find((city) => city.value === userInfo.city)}
                isSearchable={false}
                isError={!!invalidFields?.includes('city')}
                onChange={(selectedOption) =>
                  handleSelectChange('city', selectedOption?.value || '')
                }
              />
            </div>
            <div className="flex-align-center" data-field="timeSlot">
              <label className="label">Horarios:</label>
              <Select
                className="timezone"
                required
                placeholder="Franja horaria para recibir el pedido"
                isSearchable={false}
                options={timeZone}
                isError={!!invalidFields?.includes('timeSlot')}
                value={timeZone.find((tz) => tz.value === userInfo.timeSlot)}
                onChange={(selectedOption) =>
                  handleTimeSlot(selectedOption?.value || undefined)
                }
              />
            </div>
          </div>
        </section>

        {!isUserInfoScreen && (
          <>
            <section ref={scheduleRef} className="flex-column">
              <h3 className="product-title">Medio de pago</h3>
              <div className="section-card">
                {paymentSection}
                <div className="input-container margin-top-20">
                  <div className="flex gap-max-width align-start">
                    <label className="label">Descripción:</label>
                    <textarea
                      name="description"
                      ref={textareaRef}
                      className="textarea"
                      placeholder="Descripción del pedido..."
                      disabled={isUserInfoScreen}
                      value={userInfo.description}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
      {children && <div className="user-form-actions">{children}</div>}
    </div>
  );
};

export default UserInfoForm;
